import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { getKidSub } from '@/auth/authStore';
import { uploadStudyDocument } from './studyApi';
import type { CaptureSource } from './StudyCaptureFlow';
import type { StudyAnalysis } from './studyAnalysisApi';
import { StudyAnalyseConfirm } from './StudyAnalyseConfirm';
import { STUDY_PPTX_MIME } from './studyUploadTypes';
interface Prepared {
  total_slides: number;
  rendering: string;
  slides: { number: number; hidden: boolean; has_speaker_notes: boolean }[];
  pages: { document_id: string; page_number: number; title: string; image: { read_url: string } }[];
}
export function StudyPresentationCapture({
  source,
  onClose,
  onSaved,
  onAnalysed,
}: {
  source: CaptureSource;
  onClose: () => void;
  onSaved: (name: string) => void;
  onAnalysed: (analysis: StudyAnalysis) => void;
}) {
  const [owner] = useState(getKidSub);
  const [data, setData] = useState<Prepared | null>(null);
  const [chosen, setChosen] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const documentId = useRef(source.kind === 'document' ? source.document.id : null);
  const [ready, setReady] = useState(false);
  const name = source.kind === 'document' ? source.document.file_name : source.files[0].name;
  const live = useRef(true);
  const prepare = async (pages?: number[]) => {
    if (!owner || getKidSub() !== owner) return;
    setBusy(true);
    setError('');
    setReady(false);
    try {
      if (!documentId.current && source.kind === 'files') {
        const file = source.files[0];
        const upload =
          file.type === STUDY_PPTX_MIME
            ? file
            : new File([file], file.name, { type: STUDY_PPTX_MIME });
        documentId.current = (await uploadStudyDocument(upload, source.subjectKey)).id;
      }
      if (!live.current || getKidSub() !== owner) return;
      const result = await api<Prepared>(
        `/study/documents/${documentId.current}/presentation/prepare`,
        { method: 'POST', body: pages ? { pages } : {} },
      );
      if (!live.current || getKidSub() !== owner) return;
      setData(result);
      setChosen(result.pages.map((page) => page.page_number));
      setReady(true);
    } catch (failure) {
      if (live.current)
        setError(
          failure instanceof Error ? failure.message : 'The presentation could not be prepared.',
        );
    } finally {
      if (live.current) setBusy(false);
    }
  };
  // A manual action starts the free source preparation; no generation or Stars on mount.
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/35 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Prepare presentation"
        className="max-h-[92dvh] w-full max-w-3xl overflow-auto rounded-3xl bg-canvas-pure p-5"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-extrabold">{name}</h2>
          <button type="button" className="btn-pill-secondary" disabled={busy} onClick={onClose}>
            Close
          </button>
        </div>
        <p className="my-4 text-sm text-ink-soft">
          PPTX text and embedded pictures are rebuilt into learning pages. These are extracted
          pages, not original PowerPoint screenshots. Charts, SmartArt and older .ppt files need a
          PDF export.
        </p>
        {error && (
          <p role="alert" className="my-3 rounded-xl bg-wash-coral p-3">
            {error}
          </p>
        )}
        {!data && (
          <button
            type="button"
            className="btn-pill-primary"
            disabled={busy}
            onClick={() => void prepare()}
          >
            {busy ? 'Preparing slides…' : 'Prepare slides · free'}
          </button>
        )}
        {data && (
          <>
            <p className="my-3 text-sm">
              {data.total_slides} slides · choose up to 10. Hidden slides are off by default;
              selecting one includes it. Speaker notes are kept as a separately labelled source.
            </p>
            <div className="flex flex-wrap gap-2" aria-label="Select slides">
              {Array.from({ length: data.total_slides }, (_, index) => index + 1).map((number) => (
                <button
                  type="button"
                  className="btn-pill-secondary"
                  key={number}
                  aria-pressed={chosen.includes(number)}
                  disabled={busy || (!chosen.includes(number) && chosen.length >= 10)}
                  onClick={() => {
                    setChosen((current) =>
                      current.includes(number)
                        ? current.filter((n) => n !== number)
                        : [...current, number],
                    );
                    setReady(false);
                  }}
                >
                  Slide {number}
                  {data.slides.find((slide) => slide.number === number)?.hidden ? ' · hidden' : ''}
                  {data.slides.find((slide) => slide.number === number)?.has_speaker_notes
                    ? ' · notes'
                    : ''}
                </button>
              ))}
            </div>
            {!ready && (
              <button
                type="button"
                className="btn-pill-primary mt-4"
                disabled={busy || !chosen.length}
                onClick={() => void prepare(chosen)}
              >
                {busy ? 'Preparing slides…' : 'Use selected slides'}
              </button>
            )}
            {ready && (
              <>
                <div className="my-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {data.pages.map((page) => (
                    <figure key={page.page_number}>
                      <img
                        src={page.image.read_url}
                        alt={`Extracted slide ${page.page_number}`}
                        className="w-full rounded-xl border border-hairline"
                      />
                      <figcaption className="mt-2 text-xs">
                        Slide {page.page_number} · {page.title}
                      </figcaption>
                    </figure>
                  ))}
                </div>
                <StudyAnalyseConfirm
                  pages={data.pages.map((page) => ({
                    document_id: page.document_id,
                    page_number: page.page_number,
                    previewUrl: page.image.read_url,
                  }))}
                  onAnalysed={onAnalysed}
                  onSave={() => onSaved(name)}
                />
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
